import React, { useRef, lazy, memo } from 'react';
import { motion, useInView } from 'framer-motion';
import { YoutubeIcon, ExternalLinkIcon, MapIcon, MusicIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { exploreVideos as videos } from '../data/explore-videos';
export function ExploreHonduras() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true,
    margin: '-50px'
  });
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  return <section id="explore-honduras" className="bg-white py-16 lg:py-24" aria-labelledby="explore-heading" data-id="element-2176">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2177">
        {/* Header */}
        <div ref={titleRef} className="text-center mb-14" data-id="element-2178">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-2179">
            <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-2180">
              {isEs ? 'Descubre Nuestra Cultura' : 'Discover Our Culture'}
            </span>
            <h2 id="explore-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2 leading-none" data-id="element-2181">
              {isEs ? 'Explora Honduras' : 'Explore Honduras'}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-4" data-id="element-2182">
              <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-2183" />
              <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-2184" />
              <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-2185" />
            </div>
            <p className="font-nunito text-gray-600 text-lg mt-6 max-w-2xl mx-auto" data-id="element-2186">
              {isEs ? 'Descubre Honduras más allá del folleto: costas soleadas, montañas exuberantes, pueblos vibrantes y la gente cálida que da vida a cada lugar. ¡Trae tu espíritu aventurero y crea recuerdos inolvidables!' : "Discover Honduras beyond the brochure: sunlit coasts, lush mountains, vibrant towns, and the warm people who bring every place to life. Bring your sense of adventure and make memories you'll never forget!"}
            </p>
          </motion.div>
        </div>

        {/* Content Grid */}
        <div className="max-w-3xl mx-auto flex flex-col gap-6" data-id="element-2187">
          {/* Honduras Travel Link */}
          <motion.a href="https://www.honduras.travel/#" target="_blank" rel="noopener noreferrer" initial={{
          opacity: 0,
          y: 40
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="block bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 border-haa-blue group hover:shadow-xl transition-all duration-300" data-id="element-2188">
            <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-6" data-id="element-2189">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-haa-blue to-haa-midblue flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-105 transition-transform duration-300" data-id="element-2190">
                <MapIcon className="w-8 h-8 text-white" data-id="element-2191" />
              </div>
              <div className="flex-1" data-id="element-2192">
                <div className="flex items-center gap-2 mb-2" data-id="element-2193">
                  <span className="font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-blue-50 text-haa-blue" data-id="element-2194">
                    {isEs ? 'Sitio Oficial de Turismo' : 'Official Tourism Site'}
                  </span>
                </div>
                <h3 className="font-bebas text-3xl text-haa-navy tracking-wider leading-tight mb-3 group-hover:text-haa-blue transition-colors" data-id="element-2195">
                  {isEs ? 'Visita Honduras' : 'Visit Honduras'}
                </h3>
                <p className="font-nunito text-gray-600 text-base leading-relaxed mb-4" data-id="element-2196">
                  {isEs ? 'Planifica tu próxima aventura con la guía oficial de viaje de Honduras. Explora playas prístinas, antiguas ruinas mayas, exuberantes bosques nublados y ciudades vibrantes esperando ser descubiertas.' : 'Plan your next adventure with the official Honduras travel guide. Explore pristine beaches, ancient Mayan ruins, lush cloud forests, and vibrant cities waiting to be discovered.'}
                </p>
                <span className="inline-flex items-center gap-1.5 font-nunito font-bold text-sm text-haa-blue group-hover:text-haa-midblue transition-colors" data-id="element-2197">
                  {isEs ? 'Explorar Ahora' : 'Explore Now'}{' '}
                  <ExternalLinkIcon className="w-4 h-4" data-id="element-2198" />
                </span>
              </div>
            </div>
          </motion.a>

          {/* IHT — Instituto Hondureño de Turismo */}
          <motion.a href="https://www.iht.hn/en" target="_blank" rel="noopener noreferrer" initial={{
          opacity: 0,
          y: 40
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.12,
          ease: [0.16, 1, 0.3, 1]
        }} className="block bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 border-haa-midblue group hover:shadow-xl transition-all duration-300" data-id="element-2199">
            <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-6" data-id="element-2200">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-haa-midblue to-haa-navy flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-105 transition-transform duration-300" data-id="element-2201">
                <MapIcon className="w-8 h-8 text-white" data-id="element-2202" />
              </div>
              <div className="flex-1" data-id="element-2203">
                <div className="flex items-center gap-2 mb-2" data-id="element-2204">
                  <span className="font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-blue-50 text-haa-midblue" data-id="element-2205">
                    {isEs ? 'Instituto de Turismo' : 'Tourism Institute'}
                  </span>
                </div>
                <h3 className="font-bebas text-3xl text-haa-navy tracking-wider leading-tight mb-3 group-hover:text-haa-midblue transition-colors" data-id="element-2206">
                  {isEs ? 'Instituto Hondureño de Turismo' : 'Honduran Institute of Tourism'}
                </h3>
                <p className="font-nunito text-gray-600 text-base leading-relaxed mb-4" data-id="element-2207">
                  {isEs ? 'El Instituto Hondureño de Turismo (IHT) es la entidad oficial del gobierno dedicada a promover y desarrollar el turismo sostenible en Honduras. Descubre destinos, eventos y recursos turísticos.' : 'The Honduran Institute of Tourism (IHT) is the official government entity dedicated to promoting and developing sustainable tourism in Honduras. Discover destinations, events, and tourism resources.'}
                </p>
                <span className="inline-flex items-center gap-1.5 font-nunito font-bold text-sm text-haa-midblue group-hover:text-haa-navy transition-colors" data-id="element-2208">
                  {isEs ? 'Visitar Sitio' : 'Visit Site'}{' '}
                  <ExternalLinkIcon className="w-4 h-4" data-id="element-2209" />
                </span>
              </div>
            </div>
          </motion.a>

          {/* Honduras.com Travel Guides */}
          <motion.a href="https://www.honduras.com/guias/" target="_blank" rel="noopener noreferrer" initial={{
          opacity: 0,
          y: 40
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.14,
          ease: [0.16, 1, 0.3, 1]
        }} className="block bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 border-haa-skyblue group hover:shadow-xl transition-all duration-300" data-id="element-2210">
            <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-6" data-id="element-2211">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-haa-skyblue to-haa-blue flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-105 transition-transform duration-300" data-id="element-2212">
                <MapIcon className="w-8 h-8 text-white" data-id="element-2213" />
              </div>
              <div className="flex-1" data-id="element-2214">
                <div className="flex items-center gap-2 mb-2" data-id="element-2215">
                  <span className="font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-sky-50 text-haa-skyblue" data-id="element-2216">
                    {isEs ? 'Guías de Viaje' : 'Travel Guides'}
                  </span>
                </div>
                <h3 className="font-bebas text-3xl text-haa-navy tracking-wider leading-tight mb-3 group-hover:text-haa-skyblue transition-colors" data-id="element-2217">
                  Honduras.com — {isEs ? 'Guías' : 'Guides'}
                </h3>
                <p className="font-nunito text-gray-600 text-base leading-relaxed mb-4" data-id="element-2218">
                  {isEs ? 'Guías completas de viaje para explorar Honduras. Encuentra información sobre destinos, actividades, gastronomía, cultura y consejos prácticos para planificar tu visita.' : 'Comprehensive travel guides to explore Honduras. Find information on destinations, activities, cuisine, culture, and practical tips to plan your visit.'}
                </p>
                <span className="inline-flex items-center gap-1.5 font-nunito font-bold text-sm text-haa-skyblue group-hover:text-haa-blue transition-colors" data-id="element-2219">
                  {isEs ? 'Ver Guías' : 'View Guides'}{' '}
                  <ExternalLinkIcon className="w-4 h-4" data-id="element-2220" />
                </span>
              </div>
            </div>
          </motion.a>

          {/* Roatán Tourism Bureau */}
          <motion.a href="https://roatantourismbureau.com/" target="_blank" rel="noopener noreferrer" initial={{
          opacity: 0,
          y: 40
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.16,
          ease: [0.16, 1, 0.3, 1]
        }} className="block bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 border-cyan-500 group hover:shadow-xl transition-all duration-300" data-id="element-2221">
            <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-6" data-id="element-2222">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-105 transition-transform duration-300" data-id="element-2223">
                <MapIcon className="w-8 h-8 text-white" data-id="element-2224" />
              </div>
              <div className="flex-1" data-id="element-2225">
                <div className="flex items-center gap-2 mb-2" data-id="element-2226">
                  <span className="font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-cyan-50 text-cyan-600" data-id="element-2227">
                    {isEs ? 'Turismo en Roatán' : 'Roatán Tourism'}
                  </span>
                </div>
                <h3 className="font-bebas text-3xl text-haa-navy tracking-wider leading-tight mb-3 group-hover:text-cyan-600 transition-colors" data-id="element-2228">
                  {isEs ? 'Oficina de Turismo de Roatán' : 'Roatán Tourism Bureau'}
                </h3>
                <p className="font-nunito text-gray-600 text-base leading-relaxed mb-4" data-id="element-2229">
                  {isEs ? 'Descubre la isla de Roatán, la joya del Caribe hondureño. Encuentra información sobre buceo de clase mundial, playas de arena blanca, vida marina, alojamiento y actividades en las Islas de la Bahía.' : 'Discover the island of Roatán, the jewel of the Honduran Caribbean. Find information on world-class diving, white sand beaches, marine life, accommodations, and activities in the Bay Islands.'}
                </p>
                <span className="inline-flex items-center gap-1.5 font-nunito font-bold text-sm text-cyan-600 group-hover:text-teal-700 transition-colors" data-id="element-2230">
                  {isEs ? 'Descubrir Roatán' : 'Discover Roatán'}{' '}
                  <ExternalLinkIcon className="w-4 h-4" data-id="element-2231" />
                </span>
              </div>
            </div>
          </motion.a>

          {/* Videos */}
          {videos.map(video => <motion.div key={video.src} initial={{
          opacity: 0,
          y: 40
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: video.delay,
          ease: [0.16, 1, 0.3, 1]
        }} className={`bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 ${video.borderColor}`} data-id="element-2232">
              <div className="aspect-video w-full" data-id="element-2233">
                <iframe src={video.src} title={isEs ? video.titleEs : video.titleEn} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" style={{
              border: 0
            }} loading="lazy" data-id="element-2234" />
              </div>
            </motion.div>)}

          {/* Shin Fujiyama YouTube Channel */}
          <motion.a href="https://www.youtube.com/@shinfujiyamaReal/videos" target="_blank" rel="noopener noreferrer" initial={{
          opacity: 0,
          y: 40
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.28,
          ease: [0.16, 1, 0.3, 1]
        }} className="block bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 border-red-500 group hover:shadow-xl transition-all duration-300" data-id="element-2235">
            <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-6" data-id="element-2236">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-105 transition-transform duration-300" data-id="element-2237">
                <YoutubeIcon className="w-8 h-8 text-white" data-id="element-2238" />
              </div>
              <div className="flex-1" data-id="element-2239">
                <div className="flex items-center gap-2 mb-2" data-id="element-2240">
                  <span className="font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-red-50 text-red-600" data-id="element-2241">
                    {isEs ? 'Canal Destacado' : 'Featured Channel'}
                  </span>
                </div>
                <h3 className="font-bebas text-3xl text-haa-navy tracking-wider leading-tight mb-3 group-hover:text-red-600 transition-colors" data-id="element-2242">
                  {isEs ? 'Shin Fujiyama en YouTube' : 'Shin Fujiyama on YouTube'}
                </h3>
                <p className="font-nunito text-gray-600 text-base leading-relaxed mb-4" data-id="element-2243">
                  {isEs ? 'Sigue el increíble viaje de Shin Fujiyama por Honduras. Explora la rica cultura del país, sus comunidades vibrantes y paisajes impresionantes mientras aprendes sobre su misión de construir 1,000 escuelas para los niños hondureños.' : "Follow Shin Fujiyama's incredible journey across Honduras. Explore the country's rich culture, vibrant communities, and breathtaking landscapes while learning about his mission to build 1,000 schools for Honduran children."}
                </p>
                <span className="inline-flex items-center gap-1.5 font-nunito font-bold text-sm text-red-600 group-hover:text-red-700 transition-colors" data-id="element-2244">
                  {isEs ? 'Ver Videos' : 'Watch Videos'}{' '}
                  <ExternalLinkIcon className="w-4 h-4" data-id="element-2245" />
                </span>
              </div>
            </div>
          </motion.a>

          {/* Spotify — Honduran Music */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.32,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 border-emerald-500" data-id="element-2246">
            <div className="p-6 sm:p-8" data-id="element-2247">
              <div className="flex items-center gap-4 mb-5" data-id="element-2248">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center flex-shrink-0 shadow-md" data-id="element-2249">
                  <MusicIcon className="w-6 h-6 text-white" data-id="element-2250" />
                </div>
                <div data-id="element-2251">
                  <span className="font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600" data-id="element-2252">
                    {isEs ? 'Música Hondureña' : 'Honduran Music'}
                  </span>
                  <h3 className="font-bebas text-2xl text-haa-navy tracking-wider leading-tight mt-1" data-id="element-2253">
                    {isEs ? 'Escucha Honduras' : 'Listen to Honduras'}
                  </h3>
                </div>
              </div>
              <p className="font-nunito text-gray-600 text-sm leading-relaxed mb-4" data-id="element-2254">
                {isEs ? 'Sumérgete en los sonidos de Honduras — desde punta y cumbia hasta folk y artistas contemporáneos que llevan nuestra cultura al mundo.' : 'Immerse yourself in the sounds of Honduras — from punta and cumbia to folk and contemporary artists carrying our culture to the world.'}
              </p>
              <div className="rounded-xl overflow-hidden" data-id="element-2255">
                <iframe style={{
                borderRadius: '12px',
                border: 0
              }} src="https://open.spotify.com/embed/playlist/62nR2jMI03xflSzvMKjffl?utm_source=generator&theme=0" width="100%" height="352" allowFullScreen allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" title={isEs ? 'Playlist de Música Hondureña' : 'Honduran Music Playlist'} data-id="element-2256" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}